import React, { useState } from 'react'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'

const chipInputClass =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60'

type EmailChipsInputProps = {
  label: React.ReactNode
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  disabled?: boolean
  required?: boolean
  /** Limite de e-mails (padrão: ilimitado). Use 1 para um único e-mail (campo direto, sem Enter). */
  maxEmails?: number
  /** E-mails já cadastrados (ex.: people_contacts) para selecionar. */
  suggestions?: string[]
  /** Texto de ajuda curto abaixo do campo. */
  helpText?: string
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase()
}

function isLikelyEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function uniqueEmails(emails: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of emails) {
    const email = normalizeEmail(raw)
    if (!email || seen.has(email)) continue
    seen.add(email)
    out.push(email)
  }
  return out
}

export const EmailChipsInput: React.FC<EmailChipsInputProps> = ({
  label,
  values,
  onChange,
  placeholder = 'Incluir outro e-mail (Enter)',
  disabled,
  required,
  maxEmails,
  suggestions = [],
  helpText,
}) => {
  const [draft, setDraft] = useState('')
  const [hint, setHint] = useState<string | null>(null)

  const selected = uniqueEmails(values)
  const selectedSet = new Set(selected)
  const known = uniqueEmails(suggestions)
  const customSelected = selected.filter((email) => !known.includes(email))
  const singleMode = maxEmails === 1
  const singleValue = values[0] ?? ''

  const commit = (raw: string) => {
    const email = normalizeEmail(raw)
    if (!email) return
    if (!isLikelyEmail(email)) {
      setHint('Informe um e-mail válido.')
      return
    }
    if (selectedSet.has(email)) {
      setDraft('')
      setHint(null)
      return
    }
    if (typeof maxEmails === 'number' && selected.length >= maxEmails) {
      setHint(`Informe no máximo ${maxEmails} e-mail${maxEmails === 1 ? '' : 's'}.`)
      return
    }
    onChange([...selected, email])
    setDraft('')
    setHint(null)
  }

  const toggleSuggestion = (email: string) => {
    if (disabled) return
    const normalized = normalizeEmail(email)
    if (!normalized) return
    if (singleMode) {
      onChange(selectedSet.has(normalized) ? [] : [normalized])
      setHint(null)
      return
    }
    if (selectedSet.has(normalized)) {
      onChange(selected.filter((v) => v !== normalized))
      return
    }
    if (typeof maxEmails === 'number' && selected.length >= maxEmails) {
      setHint(`Informe no máximo ${maxEmails} e-mail${maxEmails === 1 ? '' : 's'}.`)
      return
    }
    onChange([...selected, normalized])
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </label>

      {known.length > 0 ? (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-3 space-y-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {singleMode
              ? 'Já cadastrados — clique para selecionar um'
              : 'Já cadastrados — clique para selecionar'}
          </p>
          <div className="flex flex-wrap gap-2">
            {known.map((email) => {
              const active = singleMode
                ? normalizeEmail(singleValue) === email
                : selectedSet.has(email)
              return (
                <button
                  key={email}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleSuggestion(email)}
                  className={[
                    'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm border transition-colors',
                    active
                      ? 'bg-primary-600 text-white border-primary-600 dark:bg-primary-500 dark:border-primary-500'
                      : 'bg-white text-gray-800 border-gray-300 hover:border-primary-400 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-600',
                    disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
                  ].join(' ')}
                  aria-pressed={active}
                >
                  {active ? <CheckIcon className="h-3.5 w-3.5 shrink-0" /> : null}
                  <span className="break-all text-left">{email}</span>
                </button>
              )
            })}
          </div>
        </div>
      ) : null}

      {singleMode ? (
        <input
          type="email"
          className={chipInputClass}
          value={singleValue}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => {
            const next = e.target.value
            onChange(next.trim() ? [next] : [])
            if (hint) setHint(null)
          }}
          onBlur={() => {
            const email = normalizeEmail(singleValue)
            if (!email) {
              onChange([])
              return
            }
            if (!isLikelyEmail(email)) {
              setHint('Informe um e-mail válido.')
              return
            }
            onChange([email])
            setHint(null)
          }}
        />
      ) : (
        <>
          <input
            type="email"
            className={chipInputClass}
            value={draft}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(e) => {
              setDraft(e.target.value)
              if (hint) setHint(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commit(draft)
              } else if (e.key === 'Backspace' && !draft && customSelected.length > 0) {
                onChange(selected.filter((v) => v !== customSelected[customSelected.length - 1]))
              }
            }}
            onBlur={() => commit(draft)}
          />

          {customSelected.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {customSelected.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 rounded-lg bg-primary-50 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200 px-2.5 py-1 text-xs font-medium"
                >
                  {email}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => onChange(selected.filter((v) => v !== email))}
                      className="hover:text-red-500"
                      aria-label={`Remover ${email}`}
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          ) : null}
        </>
      )}

      {hint ? (
        <p className="text-xs text-amber-700 dark:text-amber-300">{hint}</p>
      ) : helpText ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">{helpText}</p>
      ) : null}
    </div>
  )
}
